import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';

// Utils

// Components
import {
  Box, Button, Passwordfield, Formblock,
  // @ts-ignore
} from '@zolteam/zol-admin-ui';
// @ts-ignore
import { Form, FormElements } from '@zolteam/zol-react-form';

// Locales
import fr from '../../locales/fr.json';
import { updatePassword } from '../../services/auth/authService';

const FORM_UPDATE_PASSWORD_NAME = 'updatePassword';

/**
 * Reset Password View
 * @param {object} props
 * @param {'admin' | 'worker'} props.origin
 * @returns {JSX.Element}
 */
function UpdatePassword({ origin }) {
  const [success, setSuccess] = useState(false);
  const [passwordConfirmationError, setPasswordConfirmationError] = useState(null);
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  // Hooks
  const { t } = useTranslation();

  // schema
  const SCHEMA = {
    $schema: 'https://json-schema.org/draft/2020-12/schema',
    type: 'object',
    title: 'Login',
    properties: {
      password: {
        type: 'string',
        format: 'password',
        description: t('updatePassword.fields.password'),
        x_layout: '1/1/1/13',
        pattern: '^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[@$!%*?&.\\-])[A-Za-z0-9@$!%*?&.\\-]{8,32}$',
      },
    },
    required: [
      'password',
    ],
    additionalProperties: false,
  };

  // Services
  const {
    mutate: mutateResetPassword,
    isLoading: isLoadingResetPassword,
  } = useMutation(updatePassword, {
    onSuccess: () => {
      setSuccess(true);
    },
  });

  // Actions
  /**
   *
   * @param {object} data
   * @param {string} data.password
   * @returns {void}
   */
  const onSubmit = (data) => {
    if (!passwordConfirmationError) {
      mutateResetPassword({ newPassword: data.password, token, origin });
    }
  };

  return (
    <Box direction="column" gap="m" fullWidth>
      {success ? (
        <>
          <Box direction="column" gap="xs" align="center" fullWidth>
            <h1 className="h3-default font-weight-bold">
              {t('updatePassword.success.title')}
            </h1>
            <span className="p1-default">
              {t('updatePassword.success.description')}
            </span>
          </Box>

          <Box direction="column" gap="xs" align="normal" fullWidth>
            <Button color="noBackground">
              <Link to={origin === 'worker' ? 'goodwill://login' : '/login'}>
                {t('updatePassword.actions.login')}
              </Link>
            </Button>
          </Box>
        </>
      ) : (
        <>
          <Box direction="column" gap="xs" align="center" fullWidth>
            <h1 className="h3-default font-weight-bold">
              {t('updatePassword.title')}
            </h1>
            <span className="p1-default">
              {t('updatePassword.description')}
            </span>
          </Box>

          <Box fullWidth>
            <Form
              name={FORM_UPDATE_PASSWORD_NAME}
              className=""
              callbacks={{
                onSubmit,
              }}
              defaultValues={{
                password: '',
                passwordConfir: '',
              }}
              schema={SCHEMA}
              translations={{ formValidation: fr.formValidation }}
            >
              {
              (formConfig, formInfo) => {
                const handleBlur = () => {
                  // @ts-ignore
                  if (formInfo.formMethods.getValues().password
                  // @ts-ignore
                  !== formInfo.formMethods.getValues().passwordConfirm) {
                    // @ts-ignore
                    formInfo.formMethods.setError('passwordConfirm', { message: t('formValidation.string.passwordConfirmation') });
                    setPasswordConfirmationError(t('formValidation.string.passwordConfirmation'));
                  } else {
                    // @ts-ignore
                    formInfo.formMethods.setError('passwordConfirm', null);
                    setPasswordConfirmationError(null);
                  }
                };
                return (
                  <>
                    <FormElements
                      translations={null}
                      formConfig={formConfig}
                      formTitle={formInfo.formName}
                    />
                    <Formblock
                      key="passwordConfirm"
                      htmlFor="passwordConfirm"
                      label={t('updatePassword.fields.passwordConfirmation')}
                      message={passwordConfirmationError}
                      color={passwordConfirmationError ? 'error' : 'default'}
                    >
                      <Passwordfield
                        // @ts-ignore
                        id="passwordConfirm"
                        name="passwordConfirm"
                        placeholder={t('updatePassword.fields.passwordConfirmation')}
                        // @ts-ignore
                        value={formInfo.formMethods.getValues('passwordConfirm')}
                        hasError={!!passwordConfirmationError}
                        onBlur={handleBlur}
                        onChange={(/** @type {import('react').BaseSyntheticEvent} */ e) => {
                          const { value } = e.target;
                          // @ts-ignore
                          formInfo.formMethods.setValue('passwordConfirm', value, { shouldValidate: true });
                        }}
                      />
                    </Formblock>
                  </>
                );
              }
}
            </Form>
          </Box>

          <Box direction="column" gap="xs" align="normal" fullWidth>
            <Button
              isSubmit
              form={FORM_UPDATE_PASSWORD_NAME}
              isLoading={isLoadingResetPassword}
            >
              {t('updatePassword.actions.submit')}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
}

export default UpdatePassword;
